<script setup lang="ts">
	import { computed } from 'vue'
	import { useRoute } from 'vue-router'

	import useRooms from '../composables/useRooms'
	import { HotelRate } from '../interfaces/hotelRate'
	import SectionHeader from './ui/SectionHeader.vue'

	interface Props {
		rate: HotelRate
		className: string
	}

	const props = withDefaults(defineProps<Props>(), {
		className: '',
	})

	const route = useRoute()
	const room = useRooms(props.rate.roomType)
	const justOneBed = parseInt(room?.bedAmount as string) === 1

	// Hotelbeds: categoría oficial del proveedor, tal cual («4 ESTRELLAS», «3 LLAVES»).
	const category = computed(() => {
		if (props.rate.provider !== 'hotelbeds' || !props.rate.category) return null
		const { name, stars } = props.rate.category

		return name ?? (stars ? `${stars} estrellas` : null)
	})

	// Hotelbeds: qué habitación y qué régimen se están comprando (certificación HBX 3.1).
	const isHotelbeds = computed(() => props.rate.provider === 'hotelbeds')
	const roomName = computed(() => (isHotelbeds.value ? props.rate.roomName || null : null))
	const board = computed(() => (isHotelbeds.value ? props.rate.board || null : null))
</script>

<template>
	<div
		v-if="rate"
		:class="[
			className,
			'bg-white rounded-xl border border-gray-200 overflow-hidden',
		]"
	>
		<!-- Header -->
		<SectionHeader
			:title="props.rate.hotelName?.toUpperCase() || ''"
			subtitle="Información de la reserva"
			icon="hotel"
			icon-bg-color="blue"
			icon-color="blue"
		/>

		<!-- Info rows -->
		<div class="divide-y divide-gray-100">
			<!-- Categoría del hotel -->
			<div v-if="category" class="flex items-center gap-3 py-3 px-4">
				<span
					class="material-symbols-outlined text-[#021F58] flex-shrink-0"
					style="font-size: 18px"
					>hotel_class</span
				>
				<div>
					<p class="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
						Categoría
					</p>
					<p class="text-sm font-medium text-gray-900">
						{{ category }}
					</p>
				</div>
			</div>

			<!-- Sección: Ubicación -->
			<div class="px-4 pt-4 pb-1">
				<p class="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
					Ubicación
				</p>
			</div>

			<!-- Dirección -->
			<div class="flex items-center gap-3 py-3 px-4">
				<span
					class="material-symbols-outlined text-[#021F58] flex-shrink-0"
					style="font-size: 18px"
					>location_on</span
				>
				<div>
					<p class="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
						Dirección
					</p>
					<p class="text-sm font-medium text-gray-900">
						{{ props.rate.addresses[0]?.addressLine || 'Dirección no disponible' }}
					</p>
				</div>
			</div>

			<!-- Código Postal -->
			<div class="flex items-center gap-3 py-3 px-4">
				<span
					class="material-symbols-outlined text-[#021F58] flex-shrink-0"
					style="font-size: 18px"
					>markunread_mailbox</span
				>
				<div>
					<p class="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
						Código Postal
					</p>
					<p class="text-sm font-medium text-gray-900">
						{{ props.rate.addresses[0]?.postalCode || 'No disponible' }}
					</p>
				</div>
			</div>

			<!-- Sección: Reserva -->
			<div class="px-4 pt-4 pb-1">
				<p class="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
					Resumen de Reserva
				</p>
			</div>

			<!-- Habitación (Hotelbeds) -->
			<div v-if="roomName" class="flex items-center gap-3 py-3 px-4">
				<span
					class="material-symbols-outlined text-[#021F58] flex-shrink-0"
					style="font-size: 18px"
					>bed</span
				>
				<div>
					<p class="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
						Habitación
					</p>
					<p class="text-sm font-medium text-gray-900">
						{{ roomName }}
					</p>
				</div>
			</div>

			<!-- Régimen (Hotelbeds) -->
			<div v-if="board" class="flex items-center gap-3 py-3 px-4">
				<span
					class="material-symbols-outlined text-[#021F58] flex-shrink-0"
					style="font-size: 18px"
					>restaurant</span
				>
				<div>
					<p class="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
						Régimen
					</p>
					<p class="text-sm font-medium text-gray-900">
						{{ board }}
					</p>
				</div>
			</div>

			<!-- Habitaciones -->
			<div class="flex items-center gap-3 py-3 px-4">
				<span
					class="material-symbols-outlined text-[#021F58] flex-shrink-0"
					style="font-size: 18px"
					>meeting_room</span
				>
				<div>
					<p class="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
						Habitaciones
					</p>
					<p class="text-sm font-medium text-gray-900">
						{{ route.query.Quantity }}
						{{
							Number(route.query.Quantity) === 1 ? 'habitación' : 'habitaciones'
						}}
					</p>
				</div>
			</div>

			<!-- Huéspedes -->
			<div class="flex items-center gap-3 py-3 px-4">
				<span
					class="material-symbols-outlined text-[#021F58] flex-shrink-0"
					style="font-size: 18px"
					>group</span
				>
				<div>
					<p class="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
						Huéspedes
					</p>
					<p class="text-sm font-medium text-gray-900">
						{{ route.query.GuestCount }}
						{{
							Number(route.query.GuestCount) === 1 ? 'persona' : 'personas'
						}}
						por habitación
					</p>
				</div>
			</div>
		</div>
	</div>
</template>
